import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useState } from "react";
import { useAdmin } from "../contexts/useAdmin";

const PIN_PATTERN = /^\d{4}$/;

const PinField = ({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) => (
  <TextField
    label={label}
    type="password"
    size="small"
    value={value}
    autoFocus={autoFocus}
    autoComplete="off"
    onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 4 }}
    sx={{ width: 140 }}
  />
);

const SetPasswordForm = ({ onDone }: { onDone: () => void }) => {
  const { setPassword } = useAdmin();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!PIN_PATTERN.test(pin)) return setError("PIN must be 4 digits");
    if (pin !== confirm) return setError("PINs don't match");
    setSaving(true);
    const result = await setPassword(pin);
    setSaving(false);
    if (result.error) return setError(result.error);
    onDone();
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <PinField label="New PIN" value={pin} onChange={setPin} autoFocus />
        <PinField label="Confirm PIN" value={confirm} onChange={setConfirm} />
        <Button type="submit" variant="contained" disabled={saving}>
          Save
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </Stack>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};

// Top of the settings screen on servers with admin PIN support.
const AdminSection = () => {
  const { hasAdminPassword, stayAdmin, setStayAdmin } = useAdmin();
  const [settingPassword, setSettingPassword] = useState(false);

  let content;
  if (settingPassword) {
    content = <SetPasswordForm onDone={() => setSettingPassword(false)} />;
  } else {
    // Locked rooms never get here: Settings shows the PIN pad (AdminPinPad).
    content = (
      <>
        {!hasAdminPassword && (
          <Typography variant="caption" component="p" sx={{ mb: 1 }}>
            No admin password. Anyone can change the settings below.
          </Typography>
        )}
        {!hasAdminPassword && (
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={stayAdmin}
                  onChange={(e) => setStayAdmin(e.target.checked)}
                />
              }
              label="Administrator Override"
            />
            <Typography variant="caption" component="p">
              Makes this device admin: the guest permissions below don't apply
              to it. Without an admin password anyone can turn this on; set one
              to keep admin to yourself.
            </Typography>
          </Box>
        )}
        {hasAdminPassword && (
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={stayAdmin}
                  onChange={(e) => setStayAdmin(e.target.checked)}
                />
              }
              label="Stay admin on this device"
            />
            <Typography variant="caption" component="p">
              Saves the PIN on this device so it stays admin, even after
              reloading. When off, settings lock again as soon as you close
              them.
            </Typography>
          </Box>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setSettingPassword(true)}>
            Set admin password
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Admin
      </Typography>
      {content}
    </Box>
  );
};

export default AdminSection;
